/**
 * Fantasy Console 0 - Main Layout Component
 *
 * The editor and play views are always mounted (visibility-toggled) so their
 * console iframes stay alive across view switches. The library view is
 * conditionally rendered since it has no console to preserve.
 */

import { useRef, useState, useEffect, useCallback } from 'react'
import { useCart } from '../../context/CartContext'
import { useConsole } from '../../context/ConsoleContext'
import { Header } from './Header'
import { EditorSidebar } from './EditorSidebar'
import { TabPanel } from '../Tabs/TabPanel'
import { PlayTab } from '../Tabs/PlayTab'
import { CartLibraryTab } from '../CartLibrary'
import { ConsoleWrapper, ConsoleWrapperRef } from '../Console/ConsoleWrapper'
import { loadCodeEditorSettings } from '../CodeEditor'
import { StatusBar } from '../StatusBar/StatusBar'
import { ToastContainer } from '../Toast/ToastContainer'
import styles from './Layout.module.css'

interface LayoutProps {
  onShowShortcuts?: () => void
}

export function Layout({ onShowShortcuts }: LayoutProps) {
  const { activeView, activeTab } = useCart()
  const {
    registerActiveConsole,
    unregisterActiveConsole,
    onError,
    clearErrors,
  } = useConsole()
  const consoleRef = useRef<ConsoleWrapperRef>(null)
  const [autoRun, setAutoRun] = useState(() => loadCodeEditorSettings().autoRun)

  // Listen for editor settings changes (dispatched from EditorSettings modal)
  useEffect(() => {
    const handleSettingsChanged = () => {
      setAutoRun(loadCodeEditorSettings().autoRun)
    }
    window.addEventListener('editor-settings-changed', handleSettingsChanged)
    return () =>
      window.removeEventListener(
        'editor-settings-changed',
        handleSettingsChanged,
      )
  }, [])

  // Register the shared editor console as the active console
  useEffect(() => {
    if (activeView === 'editor') {
      registerActiveConsole({
        reload: () => consoleRef.current?.reload(),
        pauseResume: () => consoleRef.current?.pauseResume(),
        reset: () => consoleRef.current?.reset(),
      })
      return () => unregisterActiveConsole()
    }
  }, [activeView, registerActiveConsole, unregisterActiveConsole])

  // Clear errors when switching editor tabs
  useEffect(() => {
    if (activeView === 'editor') {
      clearErrors()
    }
  }, [activeTab])

  const handleError = useCallback(
    (error: import('@fc0/types').ErrorPayload) => {
      onError(error)
    },
    [onError],
  )

  return (
    <div className={styles.layout}>
      <Header />
      <div className={styles.body}>
        {/* Editor View - always mounted to preserve console iframe */}
        <div
          className={`${styles.view} ${styles.editorView} ${activeView === 'editor' ? styles.viewActive : ''}`}
        >
          <EditorSidebar />
          <main className={styles.main}>
            <TabPanel />
          </main>
          <aside className={styles.consolePane}>
            <ConsoleWrapper
              ref={consoleRef}
              size='medium'
              onError={handleError}
              autoReloadOnCodeChange={autoRun}
            />
          </aside>
        </div>

        {/* Library View - conditionally rendered (no console to preserve) */}
        {activeView === 'library' && (
          <div className={`${styles.view} ${styles.viewActive}`}>
            <main className={styles.main}>
              <CartLibraryTab />
            </main>
          </div>
        )}

        {/* Play View - always mounted to preserve console iframe */}
        <div
          className={`${styles.view} ${activeView === 'play' ? styles.viewActive : ''}`}
        >
          <main className={styles.main}>
            <PlayTab />
          </main>
        </div>
      </div>
      <StatusBar onShowShortcuts={onShowShortcuts} />
      <ToastContainer />
    </div>
  )
}
